import _ from 'lodash'

export interface ResourceLoaderDefinition {
    resourceType: string
    load(resourceIds: string[]): Promise<void | void[]>
    loadAll(): Promise<void>
    getAvailableResources(): string[]
    initAvailableResources(): Promise<void>
}
export interface ResourceStatus {
    status: 'loaded' | 'unloaded' | 'pending' | 'error'
}

export interface ResourceStatusError extends ResourceStatus {
    status: 'error'
    reason: string
}
export interface ResourceLoaderAPI {
    registerResourceLoader(resourceLoaderDefinition: ResourceLoaderDefinition): void
    load(resourceType: string, resourceIds: string[], shouldReload?: boolean): Promise<void>
    loadAll(resourceType: string): Promise<void>
    loadInitialResources(): Promise<void>
    listResources(resourceType: string): Promise<string[]> // stays async - but calls initAvailableSources
    getResourceStatus(resourceType: string, resourceId: string): ResourceStatus | ResourceStatusError
}
const createResourceLoader = (): ResourceLoaderAPI => {
    const resourceLoaders = new Map<string, ResourceLoaderDefinition>()
    const resourceStatusMaps: Record<string, Record<string, ResourceStatus | ResourceStatusError>> = {}
    const pendingResources: Record<string, Record<string, Promise<any>>> = {}
    const validateResourceType = (resourceType: string) => {
        if (!resourceLoaders.has(resourceType)) {
            throw new Error(`no resourceLoader defined for resourceType {${resourceType}}`)
        }
    }

    const validateResourceIds = (resourceType: string, resourceIds: string[]) => {
        if (!resourceIds) {
            throw new Error(`no resourced ids specified to load`)
        }
        const loader = resourceLoaders.get(resourceType)!
        const availableResources = loader.getAvailableResources()
        const missing = _.difference(resourceIds, availableResources)
        if (missing.length) {
            throw new Error(`no resources with ids [${missing}] available for resourceType {${resourceType}}`)
        }
    }

    const listResources = async (resourceType: string) => {
        validateResourceType(resourceType)
        const loader = resourceLoaders.get(resourceType)!
        if (!loader.getAvailableResources()) {
            await loader.initAvailableResources()
        }
        return loader.getAvailableResources()
    }

    const load = async (resourceType: string, requestedResourceIds: string[], shouldReload = false) => {
        validateResourceType(resourceType)
        const loader = resourceLoaders.get(resourceType)!
        validateResourceIds(resourceType, requestedResourceIds)
        const resourceStatuses = resourceStatusMaps[resourceType]
        const alreadyPending = _.pick(pendingResources[resourceType], requestedResourceIds)
        const promiseForAllPending =
            _.size(alreadyPending) > 0 ? Promise.all(_.values(alreadyPending)) : Promise.resolve()
        const filteredResources = _.filter(
            requestedResourceIds,
            id => !alreadyPending[id] && resourceStatuses[id]?.status !== 'loaded'
        )
        const resourceIdsToLoad = shouldReload ? requestedResourceIds : filteredResources
        if (!resourceIdsToLoad.length) {
            await promiseForAllPending
            return
        }
        resourceIdsToLoad.forEach(resourceId => {
            resourceStatusMaps[resourceType][resourceId] = {status: 'pending'}
        })
        let error
        try {
            const pendingResourcesPromise = loader.load(resourceIdsToLoad)
            resourceIdsToLoad.forEach(resourceId => {
                pendingResources[resourceType][resourceId] = pendingResourcesPromise
            })
            await pendingResourcesPromise
            resourceIdsToLoad.forEach(resourceId => {
                resourceStatusMaps[resourceType][resourceId] = {status: 'loaded'}
                delete pendingResources[resourceType][resourceId]
            })
            await promiseForAllPending
        } catch (e: any) {
            resourceIdsToLoad.forEach(resourceId => {
                resourceStatusMaps[resourceType][resourceId] = {status: 'error', reason: e.message}
            })
            error = e
        }

        if (error) {
            throw error
        }
    }

    const loadAll = async (resourceType: string) => {
        validateResourceType(resourceType)
        const loader = resourceLoaders.get(resourceType)!
        const resourceIdsToLoad = loader.getAvailableResources()
        resourceIdsToLoad.forEach(resourceId => {
            resourceStatusMaps[resourceType][resourceId] = {status: 'pending'}
        })

        let error
        try {
            //we use load all resources, because we want to load new resources that were added in transactions
            await loader.loadAll()
            resourceIdsToLoad.forEach(resourceId => {
                resourceStatusMaps[resourceType][resourceId] = {status: 'loaded'}
            })
        } catch (e: any) {
            resourceIdsToLoad.forEach(resourceId => {
                resourceStatusMaps[resourceType][resourceId] = {status: 'error', reason: e.message}
            })
            error = e
        }

        if (error) {
            throw error
        }
    }

    const loadInitialResources = async () => {
        for (const [resourceType, loader] of resourceLoaders.entries()) {
            try {
                validateResourceType(resourceType)
                // Note: this is now synchronous, it should probably be async, but because it would call initAvailableResources
                // once per fragment type, it would call listFragments for each type.
                // We can make those calls parallel once we can call listFragments in initializeModel - to be verified
                await loader.initAvailableResources()
            } catch (e) {
                console.error(e)
            }
        }
    }

    const registerResourceLoader = async (resourceLoaderDefinition: ResourceLoaderDefinition) => {
        resourceStatusMaps[resourceLoaderDefinition.resourceType] = {}
        pendingResources[resourceLoaderDefinition.resourceType] = {}
        resourceLoaders.set(resourceLoaderDefinition.resourceType, resourceLoaderDefinition)
    }

    const getResourceStatus = (resourceType: string, resourceId: string) => {
        const status = resourceStatusMaps[resourceType][resourceId]
        if (!status) {
            const loader = resourceLoaders.get(resourceType)!
            const availableResources = loader.getAvailableResources()
            if (!availableResources.includes(resourceId)) {
                throw new Error(
                    `no resourceLoader with id {${resourceId}} available for resourceType {${resourceType}}`
                )
            }
            return {status: 'unloaded'} satisfies ResourceStatus
        }
        return status
    }

    return {
        registerResourceLoader,
        load,
        loadAll,
        listResources,
        getResourceStatus,
        loadInitialResources
    }
}

export {createResourceLoader}
